import { goBack } from 'connected-react-router';
import { pick } from 'lodash';
import { combineReducers } from 'redux';
import { call, debounce, delay, put, putResolve, select, takeLatest } from 'redux-saga/effects';

import { api } from './api';
import { environment } from 'environments/environment';
import {
  AccessesListType,
  AccessesToCustomerTree,
  AccessesToEntity,
  AccessWithCpRefNo,
  AttemptsToVerifyContactPersonResponse,
  DeleteExternalUserAccesses,
  EntityTypeId,
  ExternalUserAccesses,
  ExternalUserAccessesFilter,
  Failure,
  HigherIdentificationAccessRequest,
  HigherIdentificationRequest,
  HigherIdentificationUpdateRequest,
  PagedAccessesToCustomerTree,
  PagedAccessesToEntity,
  SkipVerificationExternalUserAccessesRequest,
  VerifyExternalUserAccessesRequest,
} from 'generated/bffUserAccess/models';
import { ExtendedAxiosResponse } from 'helpers/apiClient';
import { KAHL_AFTER_RELOAD_PROPERTY_KEY } from 'helpers/constants/generalConstants';
import dayjs from 'helpers/dayjs';
import { decodeBase64, downloadFile, encodedBufferToUTF8 } from 'helpers/downloadHandler';
import { mimeTypeToExtension } from 'helpers/mimeType';
import {
  AppAction,
  createActionCreator,
  createActionType,
  createApiActionCreators,
  createLoadingStateReducer,
  createReducer,
  loadingActions,
  LoadingStatus,
  RequestActionTypes,
} from 'helpers/redux/reduxHelpers';
import { AppState } from 'helpers/store/models/AppState';
import { VerifyContactPersonPayload } from 'models/AccessManagement';
import { ApiClientRequest } from 'models/ApiClientRequest';
import { CustomerAccountInfo } from 'models/context/CustomerAccountInfo';
import { CustomerInfo } from 'models/context/CustomerInfo';
import { EntityAccessAssigned, ExternalUser } from 'models/context/ExternalUser';
import { TariffSpaceInfo } from 'models/context/TariffSpaceInfo';
import { GetExternalUserAccessFilter } from 'models/GetExternalUserAccessFilter';
import { LegacyCrmDeeplinkCommand, LegacyCrmDeeplinkConfig } from 'models/LegacyCrmDeeplink';
import { Pagination } from 'models/Pagination';
import { getAvailablePrivileges } from 'saga/acl/simpleUserPrivileges/ducks';
import { pushAdobeAnalyticsEventFormSubmitAction } from 'saga/adobeAnalytics/ducks';
import { getApiUrl, performApiCall } from 'saga/apiClient/ducks';
import { selectIsUserInternal } from 'saga/auth/ducks';
import { selectContextCustomerInfo } from 'saga/context/customer/ducks';
import { selectContextCustomerAccountInfo } from 'saga/context/customerAccount/ducks';
import { clearContextAction, validateContextAction } from 'saga/context/ducks';
import {
  getExternalUserDetailActions,
  selectContextExternalUser,
  selectExternalUserDetail,
  updateEntityAccessesActions,
} from 'saga/context/external/ducks';
import { selectContextTariffSpaceInfo } from 'saga/context/tariffSpace/ducks';
import { redirectToLegacyCrmAction } from 'saga/crm-deeplinks/ducks';
import { getKahlActions } from 'saga/kahl/ducks';
import { selectProperty } from 'saga/property/ducks';

/* CONSTANTS */
const debounceTime = 2000;
const CONTACT_EMAIL_STORAGE_ITEM_NAME = 'createAccessContactEmail';

export interface VerifyExternalUserAccessesPayload {
  verifyUserAccessesData: VerifyExternalUserAccessesRequest;
  skipSetContext?: boolean;
  basicPageReloadAction?: boolean;
  pageData?: {
    userClickedOnButton?: boolean;
    verifyCuRefNo?: string;
    verifyEntityType?: EntityTypeId;
    verifyEntityRefNo?: string;
    isMoreUntrustedCustomers?: boolean;
  };
}

export interface ExternalUserAccessFilterPayload {
  filter: GetExternalUserAccessFilter;
  skipDefaultKahlBehavior?: boolean;
}

export interface UpdateExternalUserAccessPayload {
  accessWithCpRefNo: AccessWithCpRefNo;
  goBack?: boolean;
}

export interface SkipVerificationExternalUsersAccessesPayload {
  skipVerificationUsersAccessesData: Partial<SkipVerificationExternalUserAccessesRequest>;
  pageData?: {
    userClickedOnButton?: boolean;
    verifyCuRefNo?: string;
    verifyEntityType?: EntityTypeId;
    verifyEntityRefNo?: string;
    isMoreUntrustedCustomers?: boolean;
  };
}

export interface DeleteExternalUserPayload {
  deleteUserAccessesData: DeleteExternalUserAccesses;
  cpRefNo?: string;
  showKahl?: boolean;
}

export interface ExternalUserAccessesDataFilter {
  defaultData: ExternalUserAccessesFilter | null;
  data: ExternalUserAccessesFilter | null;
  responseStatusError: number | null;
}

export interface AccessesToCustomerTreePayload {
  searchString?: string;
  roleType?: string;
  pushAdobeAnalyticsEvent?: boolean;
}

export interface RecordsFilterPayload {
  searchString?: string;
}

/* STATE */
export interface AccessManagementState {
  accessesToEntity: AccessesToEntity | null;
  accessesToCustomerTree: AccessesToCustomerTree | null;
  externalUserAccessesData: ExternalUserAccesses | null;
  externalUserAccessesDataFilter: ExternalUserAccessesDataFilter;
  verifyUserAccessesData: VerifyExternalUserAccessesRequest;
  loadingStatus: LoadingStatus;
  totalCount: number;
  pagination: Pagination;
  attemptsToVerifyContactPerson: AttemptsToVerifyContactPersonResponse | null;
  filter: AccessesToCustomerTreePayload;
  recordsFilter: RecordsFilterPayload;
  exportListFilter: AccessesToCustomerTreePayload;
  disableExportListButton: boolean;
}

/* ACTION TYPES */
export enum AccessManagementActionTypes {
  SetLoading = '@@AccessManagement/SET_LOADING',
  SetPage = '@@AccessManagement/SET_PAGE',
  GetAccessesToEntity = '@@AccessManagement/GET_ACCESSES_TO_ENTITY',
  GetAccessesToCustomerTree = '@@AccessManagement/GET_ACCESSES_TO_CUSTOMER_TREE',
  GetExternalUserAccessFilter = '@@AccessManagement/GET_EXTERNAL_USER_ACCESS_FILTER',
  GetExternalUserAccesses = '@@AccessManagement/GET_EXTERNAL_USER_ACCESSES',
  DeleteExternalUserAccesses = '@@AccessManagement/DELETE_EXTERNAL_USER_ACCESSES',
  VerifyExternalUserAccesses = '@@AccessManagement/VERIFY_EXTERNAL_USER_ACCESSES',
  SkipVerificationExternalUserAccesses = '@@AccessManagement/SKIP_VERIFICATION_EXTERNAL_USER_ACCESSES',
  CreateExternalUserAccess = '@@AccessManagement/CREATE_EXTERNAL_USER_ACCESSES',
  UpdateExternalUserAccess = '@@AccessManagement/UPDATE_EXTERNAL_USER_ACCESSES',
  ClearExternalUserAccess = '@@AccessManagement/CLEAR_EXTERNAL_USER_ACCESSES',
  ClearExternalUserAccessesData = '@@AccessManagement/CLEAR_EXTERNAL_USER_ACCESSES_DATA',
  GetAttemptsToVerifyContactPerson = '@@AccessManagement/GET_ATTEMPTS_TO_VERIFY_CONTACT_PERSON',
  CreateHigherIdentification = '@@AccessManagement/CREATE_HIGHER_IDENTIFICATION',
  UpdateHigherIdentification = '@@AccessManagement/UPDATE_HIGHER_IDENTIFICATION',
  DeleteHigherIdentification = '@@AccessManagement/DELETE_HIGHER_IDENTIFICATION',
  SetFilter = '@@AccessManagement/SET_FILTER',
  SetFilterDebounced = '@@AccessManagement/SET_FILTER_DEBOUNCED',
  SetRecordsFilter = '@@AccessManagement/SET_RECORDS_FILTER',
  SetExportListFilter = '@@AccessManagement/SET_EXPORT_LIST_FILTER',
  GetAccessesToCustomerTreeToFile = '@@AccessManagement/ACCESSES_TO_CUSTOMER_TREE_TO_FILE',
}

/* ACTIONS */
export const setLoadingAction = createActionCreator(AccessManagementActionTypes.SetLoading);
export const setPageAction = createActionCreator(AccessManagementActionTypes.SetPage);
export const getAccessesToEntityAction = createApiActionCreators(AccessManagementActionTypes.GetAccessesToEntity);
export const getAccessesToCustomerTreeAction = createApiActionCreators(
  AccessManagementActionTypes.GetAccessesToCustomerTree
);
export const setFilterDebouncedAction = createActionCreator(AccessManagementActionTypes.SetFilterDebounced);
export const getExternalUserAccessFilterAction = createApiActionCreators(
  AccessManagementActionTypes.GetExternalUserAccessFilter
);
export const getExternalUserAccessesAction = createApiActionCreators(
  AccessManagementActionTypes.GetExternalUserAccesses
);
export const deleteExternalUserAccessesAction = createApiActionCreators(
  AccessManagementActionTypes.DeleteExternalUserAccesses
);
export const verifyExternalUserAccessesAction = createApiActionCreators(
  AccessManagementActionTypes.VerifyExternalUserAccesses
);

export const skipVerificationExternalUserAccessesAction = createApiActionCreators(
  AccessManagementActionTypes.SkipVerificationExternalUserAccesses
);
export const createExternalUserAccessAction = createApiActionCreators(
  AccessManagementActionTypes.CreateExternalUserAccess
);
export const updateExternalUserAccessAction = createApiActionCreators(
  AccessManagementActionTypes.UpdateExternalUserAccess
);

export const clearExternalUserAccessAction = createActionCreator(AccessManagementActionTypes.ClearExternalUserAccess);

export const clearExternalUserAccessesDataAction = createActionCreator(
  AccessManagementActionTypes.ClearExternalUserAccessesData
);
export const getAttemptsToVerifyContactPersonAction = createApiActionCreators(
  AccessManagementActionTypes.GetAttemptsToVerifyContactPerson
);
export const createHigherIdentificationAction = createApiActionCreators(
  AccessManagementActionTypes.CreateHigherIdentification
);
export const updateHigherIdentificationAction = createApiActionCreators(
  AccessManagementActionTypes.UpdateHigherIdentification
);
export const deleteHigherIdentificationAction = createApiActionCreators(
  AccessManagementActionTypes.DeleteHigherIdentification
);
export const getAccessesToCustomerTreeToFileAction = createApiActionCreators(
  AccessManagementActionTypes.GetAccessesToCustomerTreeToFile
);

export const setFilterAction = createActionCreator(AccessManagementActionTypes.SetFilter);
export const setExportListFilterAction = createActionCreator(AccessManagementActionTypes.SetExportListFilter);
export const setRecordsFilterAction = createActionCreator(AccessManagementActionTypes.SetRecordsFilter);

/* REDUCERS */
const initialState: AccessManagementState = {
  accessesToEntity: null,
  loadingStatus: LoadingStatus.idle,
  accessesToCustomerTree: null,
  externalUserAccessesData: null,
  externalUserAccessesDataFilter: {
    defaultData: null,
    data: null,
    responseStatusError: null,
  },
  verifyUserAccessesData: {
    cuRefNo: '',
    verificationItem1: '',
    verificationItem2: '',
    cpRefNo: '',
  },
  totalCount: 0,
  pagination: {
    page: 1,
    pageSize: 0,
    forceCheck: false,
    initPages: 1,
  },
  attemptsToVerifyContactPerson: null,
  filter: {},
  recordsFilter: {},
  exportListFilter: {},
  disableExportListButton: false,
};

const accessesToEntity = createReducer(initialState.accessesToEntity, {
  [AccessManagementActionTypes.GetAccessesToEntity]: {
    [RequestActionTypes.SUCCESS]: (state: AccessesToEntity, payload: AccessesToEntity) => payload || null,
    [RequestActionTypes.FAILURE]: () => null,
  },
});

const loadingStatus = createLoadingStateReducer(
  initialState.loadingStatus,
  loadingActions([
    AccessManagementActionTypes.GetAccessesToEntity,
    AccessManagementActionTypes.GetAccessesToCustomerTree,
    AccessManagementActionTypes.GetAccessesToCustomerTreeToFile,
    AccessManagementActionTypes.VerifyExternalUserAccesses,
    AccessManagementActionTypes.SkipVerificationExternalUserAccesses,
    AccessManagementActionTypes.GetExternalUserAccessFilter,
  ]),
  {
    [AccessManagementActionTypes.SetLoading]: (state: LoadingStatus, status: LoadingStatus) => status,
  }
);

const totalCount = createReducer(initialState.totalCount, {
  [AccessManagementActionTypes.GetAccessesToEntity]: {
    [RequestActionTypes.SUCCESS]: (_state: number, payload: PagedAccessesToEntity) =>
      (payload.totalPages || 0) * payload.pageSize,
    [RequestActionTypes.FAILURE]: () => 0,
  },
  [AccessManagementActionTypes.GetAccessesToCustomerTree]: {
    [RequestActionTypes.SUCCESS]: (_state: number, payload: PagedAccessesToCustomerTree) =>
      payload?.accessesList?.length > 0 ? payload?.totalItems : 0,
    [RequestActionTypes.FAILURE]: () => 0,
  },
});

const pagination = createReducer(initialState.pagination, {
  [AccessManagementActionTypes.SetPage]: (_state: Pagination, payload: Pagination) => payload,
  [AccessManagementActionTypes.GetAccessesToEntity]: {
    [RequestActionTypes.SUCCESS]: (state: Pagination) => ({
      ...state,
      page: state.page < state.initPages ? state.initPages : state.page,
    }),
  },
  [AccessManagementActionTypes.SetFilter]: (state: Pagination) => ({
    ...state,
    page: initialState.pagination.page,
  }),
  [AccessManagementActionTypes.SetFilterDebounced]: (state: Pagination) => ({
    ...state,
    page: initialState.pagination.page,
  }),
  [AccessManagementActionTypes.GetAccessesToCustomerTree]: {
    [RequestActionTypes.SUCCESS]: (state: Pagination) => ({
      ...state,
      page: state.page < state.initPages ? state.initPages : state.page,
    }),
  },
  [AccessManagementActionTypes.ClearExternalUserAccess]: () => initialState.pagination,
});

const accessesToCustomerTree = createReducer(initialState.accessesToCustomerTree, {
  [AccessManagementActionTypes.GetAccessesToCustomerTree]: {
    [RequestActionTypes.SUCCESS]: (
      state: AccessesToCustomerTree,
      payload: PagedAccessesToCustomerTree & Pagination
    ) => {
      if (payload.page > 1) {
        return {
          ...state,
          accessesList: [...state.accessesList, ...payload.accessesList],
        };
      }

      return payload || [];
    },
    [RequestActionTypes.FAILURE]: () => null,
  },
  [AccessManagementActionTypes.ClearExternalUserAccess]: () => null,
});

const externalUserAccessesData = createReducer(initialState.externalUserAccessesData, {
  [AccessManagementActionTypes.GetExternalUserAccesses]: {
    [RequestActionTypes.SUCCESS]: (state: ExternalUserAccesses, payload: ExternalUserAccesses & Pagination) => {
      return payload || null;
    },
    [RequestActionTypes.FAILURE]: () => null,
  },
  [AccessManagementActionTypes.ClearExternalUserAccess]: () => null,
  [AccessManagementActionTypes.ClearExternalUserAccessesData]: () => null,
});

const externalUserAccessesDataFilter = createReducer(initialState.externalUserAccessesDataFilter, {
  [AccessManagementActionTypes.GetExternalUserAccessFilter]: {
    [RequestActionTypes.SUCCESS]: (
      state: ExternalUserAccessesDataFilter,
      payload: ExternalUserAccessesFilter & Pagination
    ) => {
      return {
        defaultData: state?.defaultData ? state?.defaultData : payload,
        data: payload || null,
        responseStatusError: 200,
      };
    },
    [RequestActionTypes.FAILURE]: (state: ExternalUserAccessesDataFilter, payload: number) => {
      return {
        defaultData: state?.defaultData,
        data: state?.defaultData,
        responseStatusError: payload,
      };
    },
  },
  [AccessManagementActionTypes.ClearExternalUserAccess]: () => null,
  [AccessManagementActionTypes.ClearExternalUserAccessesData]: () => null,
});

const verifyUserAccessesData = createReducer(initialState.verifyUserAccessesData, {
  [AccessManagementActionTypes.VerifyExternalUserAccesses]: {
    [RequestActionTypes.FAILURE]: () => initialState.verifyUserAccessesData,
  },
});

const attemptsToVerifyContactPerson = createReducer(initialState.attemptsToVerifyContactPerson, {
  [AccessManagementActionTypes.GetAttemptsToVerifyContactPerson]: {
    [RequestActionTypes.SUCCESS]: (
      state: AttemptsToVerifyContactPersonResponse,
      payload: AttemptsToVerifyContactPersonResponse
    ) => payload || null,
  },
});

const filter = createReducer(initialState.filter, {
  [AccessManagementActionTypes.SetFilter]: (
    state: AccessesToCustomerTreePayload,
    payload: Partial<AccessesToCustomerTreePayload>
  ) => ({
    ...state,
    ...payload,
  }),
  [AccessManagementActionTypes.SetFilterDebounced]: (
    state: AccessesToCustomerTreePayload,
    payload: Partial<AccessesToCustomerTreePayload>
  ) => ({
    ...state,
    ...payload,
  }),
});

const recordsFilter = createReducer(initialState.recordsFilter, {
  [AccessManagementActionTypes.SetRecordsFilter]: (
    state: RecordsFilterPayload,
    payload: Partial<RecordsFilterPayload>
  ) => ({
    ...state,
    ...payload,
  }),
});
const exportListFilter = createReducer(initialState.exportListFilter, {
  [AccessManagementActionTypes.SetExportListFilter]: (
    state: AccessesToCustomerTreePayload,
    payload: Partial<AccessesToCustomerTreePayload>
  ) => payload,
});

const disableExportListButton = createReducer(initialState.disableExportListButton, {
  [AccessManagementActionTypes.GetAccessesToCustomerTreeToFile]: {
    [RequestActionTypes.REQUEST]: () => true,
    [RequestActionTypes.SUCCESS]: () => false,
    [RequestActionTypes.FAILURE]: () => false,
  },
});

export default combineReducers<AccessManagementState>({
  accessesToEntity,
  accessesToCustomerTree,
  externalUserAccessesData,
  externalUserAccessesDataFilter,
  verifyUserAccessesData,
  loadingStatus,
  totalCount,
  pagination,
  attemptsToVerifyContactPerson,
  filter,
  recordsFilter,
  exportListFilter,
  disableExportListButton,
});

/* SELECTORS*/
const selectAccessManagement = (state: AppState) => state.accessManagement;

export const selectAccessManagementPagination = (state: AppState): Pagination =>
  selectAccessManagement(state).pagination;

export const selectAccessManagementLoading = (state: AppState): LoadingStatus =>
  selectAccessManagement(state).loadingStatus;

export const selectAccessManagementTotalCount = (state: AppState): number => selectAccessManagement(state).totalCount;

export const selectAccessToEntityData = (state: AppState): AccessesToEntity | null =>
  selectAccessManagement(state).accessesToEntity;

export const selectAccessesToCustomerTreeData = (state: AppState): AccessesToCustomerTree | null => {
  return selectAccessManagement(state).accessesToCustomerTree;
};

export const selectAccessesToCustomerTreeDataList = (state: AppState): AccessesListType[] => {
  return selectAccessManagement(state).accessesToCustomerTree?.accessesList || [];
};

export const selectAccessExternalUserAccessesData = (state: AppState): ExternalUserAccesses | null => {
  return selectAccessManagement(state).externalUserAccessesData;
};

export const selectAccessExternalUserAccessesFilterData = (state: AppState): ExternalUserAccessesFilter | null => {
  return selectAccessManagement(state).externalUserAccessesDataFilter?.data;
};

export const selectAccessExternalUserAccessesFilterDataResponseStatus = (state: AppState): number | null => {
  return selectAccessManagement(state).externalUserAccessesDataFilter?.responseStatusError;
};

export const selectVerifyUserAccessesData = (state: AppState): VerifyExternalUserAccessesRequest =>
  selectAccessManagement(state).verifyUserAccessesData;

export const selectAttemptsToVerifyContactPerson = (state: AppState): AttemptsToVerifyContactPersonResponse | null =>
  selectAccessManagement(state).attemptsToVerifyContactPerson;

export const selectAccessManagementFilter = (state: AppState): AccessesToCustomerTreePayload | null =>
  selectAccessManagement(state).filter;

export const selectRecordsFilter = (state: AppState): RecordsFilterPayload | null =>
  selectAccessManagement(state).recordsFilter;

export const selectExportListFilter = (state: AppState): AccessesToCustomerTreePayload | null =>
  selectAccessManagement(state).exportListFilter;

export const selectDisableExportListButton = (state: AppState): boolean =>
  selectAccessManagement(state).disableExportListButton;

/* SAGAS */
function* getAccessManagementDefaultPagination() {
  const pageSize = Number(selectProperty('gui_accessManagement_cuContPersons.pageSize'));
  const pagination: Pagination = yield select(selectAccessManagementPagination);

  yield put(
    setPageAction({
      ...pagination,
      pageSize: pageSize || 10,
    })
  );
}

function* getAccessesToEntity() {
  yield put(setLoadingAction(LoadingStatus.loading));

  const contextCustomer: CustomerInfo = yield select(selectContextCustomerInfo);
  const contextCuRefNo = contextCustomer?.cuRefNo;
  const contextCustomerAccount: CustomerAccountInfo = yield select(selectContextCustomerAccountInfo);
  const contextTariffSpace: TariffSpaceInfo = yield select(selectContextTariffSpaceInfo);
  const recordsFilterPayload: RecordsFilterPayload = yield select(selectRecordsFilter);
  const searchString: string = recordsFilterPayload.searchString ? recordsFilterPayload.searchString : '';

  let entityRefNo: string;
  let entityTypeId: string;

  if (contextTariffSpace) {
    entityRefNo = contextTariffSpace.tsRefNo!;
    entityTypeId = EntityTypeId.TariffSpace;
  } else if (contextCustomerAccount) {
    entityRefNo = contextCustomerAccount.caRefNo;
    entityTypeId = EntityTypeId.CustomerAccount;
  } else {
    entityRefNo = contextCuRefNo;
    entityTypeId = EntityTypeId.Customer;
  }

  const request: ApiClientRequest = {
    apiCall: call(
      api.getAccessesToEntity,
      yield getApiUrl(environment.urlConfig.userAccess),
      contextCuRefNo,
      entityRefNo,
      entityTypeId,
      searchString
    ),
  };
  const resp: ExtendedAxiosResponse = yield call(performApiCall, request);

  if (resp.ok) {
    yield put(
      getAccessesToEntityAction.success({
        ...resp.data,
      })
    );
  } else {
    yield put(getAccessesToEntityAction.failure());
  }
}

function* getAccessesToCustomerTree({ payload }: AppAction<Partial<Pagination> & AccessesToCustomerTreePayload>) {
  if (payload.pushAdobeAnalyticsEvent) {
    yield put(pushAdobeAnalyticsEventFormSubmitAction('2-3-1-2-4-filterAccess'));
  }

  const user: ExternalUser = yield select(selectContextExternalUser);
  const contextCustomer: CustomerInfo = yield select(selectContextCustomerInfo);
  const filter: AccessesToCustomerTreePayload = yield select(selectAccessManagementFilter);

  const pagination: Pagination = yield select(selectAccessManagementPagination);

  if (!pagination.pageSize) {
    yield call(getAccessManagementDefaultPagination);

    return;
  }

  const payloadPagination: Partial<Pagination> = pick(payload, ['page']);

  const basePagination = {
    ...pagination,
    ...payloadPagination,
  };

  const paginationParam = {
    ...basePagination,
    page: basePagination.page < basePagination.initPages ? 1 : basePagination.page,
    pageSize:
      basePagination.page < basePagination.initPages
        ? basePagination.pageSize * basePagination.initPages
        : basePagination.pageSize,
  };

  const request: ApiClientRequest = {
    apiCall: call(
      api.getAccessesToCustomerTree,
      yield getApiUrl(environment.urlConfig.userAccess),
      contextCustomer?.cuRefNo,
      paginationParam,
      user?.cpRefNo,
      filter
    ),
  };
  const resp: ExtendedAxiosResponse = yield call(performApiCall, request);

  if (resp.ok) {
    yield put(
      getAccessesToCustomerTreeAction.success({
        ...resp.data,
        ...pagination,
      })
    );
  } else {
    yield put(getAccessesToCustomerTreeAction.failure());
  }
}

export function* getAccessesToCustomerTreeByCuRefNo(cuRefNo: string) {
  if (cuRefNo) {
    const request: ApiClientRequest = {
      apiCall: call(api.getAccessesToCustomerTree, yield getApiUrl(environment.urlConfig.userAccess), cuRefNo),
    };
    const resp: ExtendedAxiosResponse = yield call(performApiCall, request);

    if (resp.ok) {
      return resp.data;
    }
  }

  return null;
}

function* getExternalUserAccesses({ payload: contactPersonRefNo }: any) {
  const contextCustomer: CustomerInfo | null = yield select(selectContextCustomerInfo);

  if (!contextCustomer?.cuRefNo) {
    return;
  }

  yield put(setLoadingAction(LoadingStatus.loading));

  const request: ApiClientRequest = {
    apiCall: call(
      api.getExternalUserAccesses,
      yield getApiUrl(environment.urlConfig.userAccess),
      contactPersonRefNo,
      contextCustomer?.cuRefNo
    ),
  };
  const resp: ExtendedAxiosResponse = yield call(performApiCall, request);

  if (resp.ok) {
    yield put(
      getExternalUserAccessesAction.success({
        ...resp.data,
        ...pagination,
      })
    );
  } else {
    yield put(getExternalUserAccessesAction.failure());
  }
}

function* deleteExternalUserAccesses({ payload }: AppAction<DeleteExternalUserPayload>) {
  const user: ExternalUser = yield select(selectContextExternalUser);
  const userDetail: ExternalUser = yield select(selectExternalUserDetail);
  const contextCustomer: CustomerInfo = yield select(selectContextCustomerInfo);

  const externalUserData = userDetail ? userDetail : user;

  yield put(setLoadingAction(LoadingStatus.loading));

  const request: ApiClientRequest = {
    apiCall: call(
      api.deleteExternalUserAccesses,
      yield getApiUrl(environment.urlConfig.userAccess),
      payload.deleteUserAccessesData
    ),
  };
  const resp: ExtendedAxiosResponse = yield call(performApiCall, request);

  if (resp.ok) {
    yield put(deleteExternalUserAccessesAction.success());

    if (payload?.showKahl) {
      yield put(getKahlActions.request({ infoMessageIds: resp?.data?.kahlId }));
    }

    if (
      payload?.deleteUserAccessesData?.cpAccessesList?.[0]?.accessToEntityRef?.length === 0 ||
      externalUserData?.entityAccesses?.some(
        (entity) =>
          entity.cuRefNo === contextCustomer.cuRefNo &&
          payload.deleteUserAccessesData?.cpAccessesList?.some((cpAccess) =>
            cpAccess?.accessToEntityRef?.some(
              (accessToEntity) =>
                accessToEntity.entityRefNo === entity.entityRefNo && accessToEntity.roleType === entity.roleType
            )
          )
      )
    ) {
      yield put(clearContextAction.request());

      const deeplinkParams: LegacyCrmDeeplinkConfig = {
        command: LegacyCrmDeeplinkCommand.IMO2DASH,
        queryParams: {
          page: 'internalDashboard',
          //eslint-disable-next-line
          _dashboard_WAR_customercareportlets_logToVisitedHistory: 'true',
        },
      };

      yield putResolve(redirectToLegacyCrmAction(deeplinkParams));
    } else {
      yield put(
        getExternalUserDetailActions.request({
          cpRefNo: user?.cpRefNo || '',
          forceCheck: true,
        })
      );

      // Reload data on delete
      if (payload?.cpRefNo) {
        // 2.19.3
        yield put(clearExternalUserAccessAction());
        yield put(getExternalUserAccessesAction.request(payload.cpRefNo));
      } else {
        // 2.3.1.2.4
        yield put(clearExternalUserAccessAction());
        yield put(getAccessesToCustomerTreeAction.request({ page: 1 }));
      }
    }
  } else {
    yield put(deleteExternalUserAccessesAction.failure());
  }
}

/**
 * EA: Projects.O2 Czech Republic.MO2-CARE.Processes.Modules.AccessManagement.verifyAccessToEntity.Verify - actionOnClick
 */
function* verifyExternalUserAccesses({ payload }: AppAction<VerifyExternalUserAccessesPayload>) {
  yield put(setLoadingAction(LoadingStatus.loading));

  const request: ApiClientRequest = {
    apiCall: call(
      api.putVerifyExternalUserAccesses,
      yield getApiUrl(environment.urlConfig.userAccess),
      payload.verifyUserAccessesData
    ),
  };
  const resp: ExtendedAxiosResponse = yield call(performApiCall, request);

  if (resp.ok) {
    if (!payload.skipSetContext) {
      const verifyExtUserResponse: { isTrustedSetToTrue: boolean } = resp.data;

      if (verifyExtUserResponse && verifyExtUserResponse.isTrustedSetToTrue) {
        yield put(verifyExternalUserAccessesAction.success());

        if (payload.basicPageReloadAction) {
          window.location.reload();
        } else if (payload.pageData) {
          yield call(setContextAndRedirectAfterVerification, payload.pageData);
        }
      } else {
        yield put(verifyExternalUserAccessesAction.failure());
        localStorage.setItem(KAHL_AFTER_RELOAD_PROPERTY_KEY, 'ID1031');
        window.location.reload();
      }
    } else {
      yield put(verifyExternalUserAccessesAction.success());

      if (payload.basicPageReloadAction) {
        window.location.reload();
      } else if (payload.pageData) {
        yield call(setContextAndRedirectAfterVerification, payload?.pageData);
      }
    }
  } else {
    yield put(verifyExternalUserAccessesAction.failure());
  }
}

function* skipVerificationExternalUserAccesses({ payload }: AppAction<SkipVerificationExternalUsersAccessesPayload>) {
  yield put(setLoadingAction(LoadingStatus.loading));

  const request: ApiClientRequest = {
    apiCall: call(
      api.putSkipVerificationExternalUserAccesses,
      yield getApiUrl(environment.urlConfig.userAccess),
      payload.skipVerificationUsersAccessesData
    ),
  };
  const resp: ExtendedAxiosResponse = yield call(performApiCall, request);

  if (resp.ok) {
    yield put(skipVerificationExternalUserAccessesAction.success());

    if (payload?.pageData) {
      yield call(setContextAndRedirectAfterVerification, payload.pageData);
    }
  } else {
    yield put(skipVerificationExternalUserAccessesAction.failure());
    yield put(getKahlActions.request());
  }
}

function* getExternalUserAccessFilter({ payload }: AppAction<ExternalUserAccessFilterPayload>) {
  const contextCustomer: CustomerInfo | null = yield select(selectContextCustomerInfo);

  yield put(setLoadingAction(LoadingStatus.loading));

  const request: ApiClientRequest = {
    apiCall: call(
      api.getExternalUserAccessFilter,
      yield getApiUrl(environment.urlConfig.userAccess),
      payload?.filter,
      contextCustomer?.cuRefNo
    ),
    skipDefaultKahlBehavior: payload?.skipDefaultKahlBehavior,
  };
  const resp: ExtendedAxiosResponse = yield call(performApiCall, request);

  if (resp.ok) {
    yield put(
      getExternalUserAccessFilterAction.success({
        ...resp.data,
        ...pagination,
      })
    );
    yield put(setLoadingAction(LoadingStatus.success));
  } else {
    yield put(getExternalUserAccessFilterAction.failure(resp.status));
    yield put(setLoadingAction(LoadingStatus.failure));
  }
}

function* createExternalUserAccess({ payload: createUserAccess }: any) {
  const user: ExternalUser = yield select(selectContextExternalUser);
  const contextCustomer: CustomerInfo = yield select(selectContextCustomerInfo);

  if (!createUserAccess) return;

  yield put(setLoadingAction(LoadingStatus.loading));

  const request: ApiClientRequest = {
    apiCall: call(api.createExternalUserAccess, yield getApiUrl(environment.urlConfig.userAccess), createUserAccess),
    skipDefaultKahlBehavior: true,
  };
  const resp: ExtendedAxiosResponse = yield call(performApiCall, request);

  if (resp.ok) {
    yield put(createExternalUserAccessAction.success());
    yield put(setLoadingAction(LoadingStatus.success));

    if (localStorage.getItem(CONTACT_EMAIL_STORAGE_ITEM_NAME)) {
      localStorage.removeItem(CONTACT_EMAIL_STORAGE_ITEM_NAME);
    }

    yield put(getKahlActions.request({ infoMessageIds: resp?.data?.kahlId }));

    const isNewPrimaryAdmin: boolean = createUserAccess.accessToEntityRef.roleType === 'E110';

    if (
      isNewPrimaryAdmin &&
      user?.entityAccesses?.some((entity) => entity.roleType === 'E110' && entity.cuRefNo === contextCustomer.cuRefNo)
    ) {
      yield put(clearContextAction.request());

      const deeplinkParams: LegacyCrmDeeplinkConfig = {
        command: LegacyCrmDeeplinkCommand.IMO2DASH,
        queryParams: {
          page: 'internalDashboard',
          //eslint-disable-next-line
          _dashboard_WAR_customercareportlets_logToVisitedHistory: 'true',
        },
      };

      yield putResolve(redirectToLegacyCrmAction(deeplinkParams));
    } else {
      yield put(goBack());
    }
  } else if (resp.status === 412) {
    const stolenError = resp.data?.failures?.findIndex(
      (failure: Failure) => failure.contextData?.idNumber === 'STOLEN_ERROR'
    );

    if (stolenError !== -1) {
      yield put(getKahlActions.request({ infoMessageIds: 'ID1560' }));
      yield put(setLoadingAction(LoadingStatus.failure));
    }

    yield put(updateHigherIdentificationAction.failure());
  } else {
    yield put(getKahlActions.request({ infoMessageIds: 'ID0013' }));
    yield put(updateHigherIdentificationAction.failure());
  }
}

function* updateExternalUserAccess({ payload }: AppAction<UpdateExternalUserAccessPayload>) {
  const user: ExternalUser = yield select(selectContextExternalUser);
  const contextCustomer: CustomerInfo = yield select(selectContextCustomerInfo);

  yield put(setLoadingAction(LoadingStatus.loading));

  const request: ApiClientRequest = {
    apiCall: call(
      api.updateExternalUserAccess,
      yield getApiUrl(environment.urlConfig.userAccess),
      payload?.accessWithCpRefNo
    ),
  };
  const resp: ExtendedAxiosResponse = yield call(performApiCall, request);

  if (resp.ok) {
    yield put(updateExternalUserAccessAction.success());
    yield put(setLoadingAction(LoadingStatus.success));

    if (localStorage.getItem(CONTACT_EMAIL_STORAGE_ITEM_NAME)) {
      localStorage.removeItem(CONTACT_EMAIL_STORAGE_ITEM_NAME);
    }

    yield put(getKahlActions.request({ infoMessageIds: resp?.data?.kahlId }));

    const isNewPrimaryAdmin: boolean = payload.accessWithCpRefNo.accessToEntityRef.roleType === 'E110';

    if (
      isNewPrimaryAdmin &&
      user?.entityAccesses?.some((entity) => entity.roleType === 'E110' && entity.cuRefNo === contextCustomer.cuRefNo)
    ) {
      yield put(clearContextAction.request());

      const deeplinkParams: LegacyCrmDeeplinkConfig = {
        command: LegacyCrmDeeplinkCommand.IMO2DASH,
        queryParams: {
          page: 'internalDashboard',
          //eslint-disable-next-line
          _dashboard_WAR_customercareportlets_logToVisitedHistory: 'true',
        },
      };

      yield putResolve(redirectToLegacyCrmAction(deeplinkParams));
    } else if (payload?.goBack) {
      yield put(goBack());
    }

    yield put(getExternalUserAccessesAction.request(payload?.accessWithCpRefNo?.contactPersonRefNo));
  } else {
    yield put(updateExternalUserAccessAction.failure());
    yield put(setLoadingAction(LoadingStatus.failure));
  }
}

function* createHigherIdentification({ payload }: AppAction<HigherIdentificationRequest>) {
  yield put(setLoadingAction(LoadingStatus.loading));

  const request: ApiClientRequest = {
    apiCall: call(api.createHigherIdentification, yield getApiUrl(environment.urlConfig.userAccess), payload),
    skipUserActivityLog: true,
    skipDefaultKahlBehavior: true,
  };
  const resp: ExtendedAxiosResponse = yield call(performApiCall, request);

  if (resp.ok) {
    yield put(createHigherIdentificationAction.success());
    localStorage.setItem(KAHL_AFTER_RELOAD_PROPERTY_KEY, 'ID0923');
    yield put(setLoadingAction(LoadingStatus.success));
    window.location.reload();
  } else if (resp.status === 412) {
    const stolenError = resp.data?.failures?.findIndex(
      (failure: Failure) => failure.contextData?.idNumber === 'STOLEN_ERROR'
    );

    if (stolenError !== -1) {
      yield put(getKahlActions.request({ infoMessageIds: 'ID1560' }));
      yield put(setLoadingAction(LoadingStatus.failure));
    }

    yield put(updateHigherIdentificationAction.failure());
  } else {
    yield put(getKahlActions.request({ infoMessageIds: 'ID0013' }));
    yield put(updateHigherIdentificationAction.failure());
  }
}

function* updateHigherIdentification({ payload }: AppAction<HigherIdentificationUpdateRequest>) {
  yield put(setLoadingAction(LoadingStatus.loading));

  const request: ApiClientRequest = {
    apiCall: call(api.updateHigherIdentification, yield getApiUrl(environment.urlConfig.userAccess), payload),
    skipDefaultKahlBehavior: true,
  };
  const resp: ExtendedAxiosResponse = yield call(performApiCall, request);

  if (resp.ok) {
    yield put(updateHigherIdentificationAction.success());
    yield put(getKahlActions.request({ infoMessageIds: 'ID1559' }));
    yield put(setLoadingAction(LoadingStatus.success));
  } else if (resp.status === 412) {
    const stolenError = resp.data?.failures?.findIndex(
      (failure: Failure) => failure.contextData?.idNumber === 'STOLEN_ERROR'
    );

    if (stolenError !== -1) {
      yield put(getKahlActions.request({ infoMessageIds: 'ID1560' }));
      yield put(setLoadingAction(LoadingStatus.failure));
    }

    yield put(updateHigherIdentificationAction.failure());
  } else {
    yield put(getKahlActions.request({ infoMessageIds: 'ID0013' }));
    yield put(updateHigherIdentificationAction.failure());
  }
}

function* deleteHigherIdentification({ payload }: AppAction<HigherIdentificationAccessRequest>) {
  yield put(setLoadingAction(LoadingStatus.loading));

  const request: ApiClientRequest = {
    apiCall: call(api.deleteHigherIdentification, yield getApiUrl(environment.urlConfig.userAccess), payload),
  };
  const resp: ExtendedAxiosResponse = yield call(performApiCall, request);

  if (resp.ok) {
    yield put(deleteHigherIdentificationAction.success());
  } else {
    yield put(deleteHigherIdentificationAction.failure());
  }
}

function* getAccessesToCustomerTreeToFile({ payload: tryAgain = false }: AppAction<boolean>) {
  yield put(setLoadingAction(LoadingStatus.loading));

  const user: ExternalUser = yield select(selectContextExternalUser);
  const contextCustomer: CustomerInfo = yield select(selectContextCustomerInfo);
  const filter: AccessesToCustomerTreePayload = yield select(selectAccessManagementFilter);

  if (!tryAgain) {
    yield put(setExportListFilterAction(filter));
  }

  const initFilter: AccessesToCustomerTreePayload = yield select(selectExportListFilter);
  const timeToGetFile: string = selectProperty('gui.entityAccessManagement.AccessToEntity.timeToGetFile');
  const request: ApiClientRequest = {
    apiCall: call(
      api.getAccessesToCustomerTreeToFile,
      yield getApiUrl(environment.urlConfig.userAccess),
      contextCustomer?.cuRefNo,
      user?.cpRefNo,
      tryAgain ? initFilter : filter
    ),
  };
  const resp: ExtendedAxiosResponse = yield call(performApiCall, request);

  if (resp.status === 202) {
    if (!tryAgain) {
      yield put(getKahlActions.request({ infoMessageIds: 'ID1595' }));
    }

    yield delay(Number(timeToGetFile) * 1000);
    yield put(getAccessesToCustomerTreeToFileAction.request(true));
  } else if (resp.ok && resp.data) {
    const { fileContent, mimeType } = resp.data;
    const decoded =
      mimeType === 'text/csv'
        ? encodedBufferToUTF8(decodeBase64(fileContent.toString()), 'UTF-8', true)
        : decodeBase64(fileContent.toString());

    downloadFile(
      decoded,
      `${dayjs().format('YYYYMMDD')}_Export podřízených kontaktních osob.${mimeTypeToExtension(mimeType)}`,
      mimeType
    );
    yield put(getAccessesToCustomerTreeToFileAction.success());
  } else {
    yield put(getAccessesToCustomerTreeToFileAction.failure());
  }
}

function* getAttemptsToVerifyContactPerson({ payload }: AppAction<VerifyContactPersonPayload>) {
  yield put(setLoadingAction(LoadingStatus.loading));

  const request: ApiClientRequest = {
    apiCall: call(
      api.getAttemptsToVerifyContactPerson,
      yield getApiUrl(environment.urlConfig.userAccess),
      payload.cuRefNo,
      payload.cpRefNo
    ),
  };
  const resp: ExtendedAxiosResponse = yield call(performApiCall, request);

  if (resp.ok) {
    yield put(getAttemptsToVerifyContactPersonAction.success(resp.data));
  } else {
    yield put(getAttemptsToVerifyContactPersonAction.failure());
  }
}

function* setContextAndRedirectAfterVerification(payload: {
  userClickedOnButton?: boolean;
  verifyCuRefNo?: string;
  verifyEntityType?: EntityTypeId;
  verifyEntityRefNo?: string;
  validateContextExternalUser?: boolean;
  isMoreUntrustedCustomers?: boolean;
}) {
  const urlParams = new URLSearchParams(window.location.search);
  const cpRefNo = urlParams.get('cpRefNo');
  const redirectUrl = urlParams.get('redirectUrl');

  if (cpRefNo) {
    // Verify ExternalUser
    const isInternalUser: boolean = yield select(selectIsUserInternal);
    const availablePrivileges: Record<string, boolean> = yield call(getAvailablePrivileges, '', [
      'SKIP_IDENTIFICATION',
    ]);
    const SKIP_IDENTIFICATION = availablePrivileges['SKIP_IDENTIFICATION'];

    if (!SKIP_IDENTIFICATION && payload.userClickedOnButton) {
      // Go to user verification 4.3.3
      const deeplinkParams: LegacyCrmDeeplinkConfig = {
        command: isInternalUser ? LegacyCrmDeeplinkCommand.IMO2DASH : LegacyCrmDeeplinkCommand.EMO2DASH,
        queryParams: {
          caRefNo: cpRefNo,
          page: 'contactPersonVerification',
        },
      };

      yield putResolve(redirectToLegacyCrmAction(deeplinkParams));
    }
  } else if (!payload.validateContextExternalUser && !payload.isMoreUntrustedCustomers) {
    // Set temp verification to session
    const contextExtUser: ExternalUser = yield select(selectContextExternalUser);
    const entityAccesses = contextExtUser.entityAccesses;

    if (entityAccesses) {
      let index: number;

      if (payload.verifyEntityType === EntityTypeId.Customer) {
        index = entityAccesses?.findIndex(
          (access: EntityAccessAssigned) => access.cuRefNo === payload.verifyEntityRefNo
        );
      } else {
        index = entityAccesses?.findIndex(
          (access: EntityAccessAssigned) =>
            access.entityRefNo === payload.verifyEntityRefNo && access.entityType === payload.verifyEntityType
        );
      }

      if (index !== -1) {
        entityAccesses[index].isVerified = true;
        yield putResolve(updateEntityAccessesActions.success(entityAccesses));
      }
    }

    if (payload.verifyEntityType === EntityTypeId.Customer) {
      yield putResolve(
        validateContextAction.request({
          cuRefNo: payload.verifyEntityRefNo,
        })
      );
    }

    if (payload.verifyEntityType === EntityTypeId.CustomerAccount) {
      yield putResolve(
        validateContextAction.request({
          caRefNo: payload.verifyEntityRefNo,
        })
      );
    }

    if (payload.verifyEntityType === EntityTypeId.TariffSpace) {
      yield putResolve(
        validateContextAction.request({
          tsRefNo: payload.verifyEntityRefNo,
        })
      );
    }
  }

  if (
    redirectUrl &&
    (payload.verifyEntityType !== EntityTypeId.Customer || payload.isMoreUntrustedCustomers || cpRefNo)
  ) {
    // Redirect to filled URL
    window.location.href = redirectUrl;
  } else {
    // Redirect to dashboard 2.1.1
    const isInternalUser: boolean = yield select(selectIsUserInternal);
    const deeplinkParams: LegacyCrmDeeplinkConfig = {
      command: isInternalUser ? LegacyCrmDeeplinkCommand.IMO2DASH : LegacyCrmDeeplinkCommand.EMO2DASH,
      queryParams: {
        cuRefNo: payload.verifyCuRefNo,
      },
    };

    yield putResolve(redirectToLegacyCrmAction(deeplinkParams));
  }
}

/* EXPORT */
export function* accessManagementSaga() {
  yield takeLatest(
    createActionType(AccessManagementActionTypes.GetAccessesToEntity, RequestActionTypes.REQUEST),
    getAccessesToEntity
  );

  yield takeLatest(
    [
      createActionType(AccessManagementActionTypes.GetAccessesToCustomerTree, RequestActionTypes.REQUEST),
      AccessManagementActionTypes.SetPage,
      AccessManagementActionTypes.SetFilter,
    ],
    getAccessesToCustomerTree
  );

  yield takeLatest(
    createActionType(AccessManagementActionTypes.GetExternalUserAccessFilter, RequestActionTypes.REQUEST),
    getExternalUserAccessFilter
  );

  yield takeLatest(
    createActionType(AccessManagementActionTypes.GetExternalUserAccesses, RequestActionTypes.REQUEST),
    getExternalUserAccesses
  );

  yield takeLatest(
    createActionType(AccessManagementActionTypes.DeleteExternalUserAccesses, RequestActionTypes.REQUEST),
    deleteExternalUserAccesses
  );

  yield takeLatest(
    [
      createActionType(AccessManagementActionTypes.CreateExternalUserAccess, RequestActionTypes.REQUEST),
      AccessManagementActionTypes.ClearExternalUserAccess,
    ],
    createExternalUserAccess
  );

  yield takeLatest(
    createActionType(AccessManagementActionTypes.UpdateExternalUserAccess, RequestActionTypes.REQUEST),
    updateExternalUserAccess
  );

  yield takeLatest(
    createActionType(AccessManagementActionTypes.VerifyExternalUserAccesses, RequestActionTypes.REQUEST),
    verifyExternalUserAccesses
  );

  yield takeLatest(
    createActionType(AccessManagementActionTypes.SkipVerificationExternalUserAccesses, RequestActionTypes.REQUEST),
    skipVerificationExternalUserAccesses
  );

  yield takeLatest(
    createActionType(AccessManagementActionTypes.GetAttemptsToVerifyContactPerson, RequestActionTypes.REQUEST),
    getAttemptsToVerifyContactPerson
  );

  yield takeLatest(
    createActionType(AccessManagementActionTypes.CreateHigherIdentification, RequestActionTypes.REQUEST),
    createHigherIdentification
  );
  yield takeLatest(
    createActionType(AccessManagementActionTypes.UpdateHigherIdentification, RequestActionTypes.REQUEST),
    updateHigherIdentification
  );
  yield takeLatest(
    createActionType(AccessManagementActionTypes.DeleteHigherIdentification, RequestActionTypes.REQUEST),
    deleteHigherIdentification
  );

  yield debounce(debounceTime, AccessManagementActionTypes.SetFilterDebounced, getAccessesToCustomerTree);

  yield takeLatest(
    createActionType(AccessManagementActionTypes.GetAccessesToCustomerTreeToFile, RequestActionTypes.REQUEST),
    getAccessesToCustomerTreeToFile
  );
}
